import {
    AUTH_JWT_TOKEN
} from "@compass/core";
import * as signalR from "@microsoft/signalr";
import {
    HubName
} from "generated/openApi";
import { useEffect, useState } from "react";

export const useTextingConversationsHubConnection = () => {
  const [connectionStatus, setConnectionStatus] = useState<signalR.HubConnectionState>(
    signalR.HubConnectionState.Connecting
  );

  const [textingConversationsHubConnection] = useState<signalR.HubConnection>(
    () => new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_TEXTING_API_ENDPOINT}/${HubName[HubName.TextingConversationsHub]}`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => localStorage.getItem(AUTH_JWT_TOKEN) ?? "",
      })
      .withAutomaticReconnect()
      .build()
  );

  // register connection
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRetry = () => {
      if (cancelled || retryTimer !== undefined) return;

      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        startConnection();
      }, 5000);
    };

    const startConnection = async () => {
      if (cancelled) return;
      if (textingConversationsHubConnection.state === signalR.HubConnectionState.Connected) {
        setConnectionStatus(signalR.HubConnectionState.Connected);
        return;
      }
      if (textingConversationsHubConnection.state !== signalR.HubConnectionState.Disconnected) {
        scheduleRetry();
        return;
      }

      setConnectionStatus(signalR.HubConnectionState.Connecting);
      try {
        await textingConversationsHubConnection.start();
        if (!cancelled) setConnectionStatus(textingConversationsHubConnection.state);
      } catch (error) {
        if (cancelled) return;
        console.error("Texting conversation connection failed; retrying:", error);
        setConnectionStatus(signalR.HubConnectionState.Disconnected);
        scheduleRetry();
      }
    };

    // Set up event handlers for connection state changes
    textingConversationsHubConnection.onclose(() => {
      if (cancelled) return;
      setConnectionStatus(signalR.HubConnectionState.Disconnected);
      scheduleRetry();
    });

    textingConversationsHubConnection.onreconnecting(() => {
      if (cancelled) return;
      setConnectionStatus(signalR.HubConnectionState.Reconnecting);
    });

    textingConversationsHubConnection.onreconnected(() => {
      if (cancelled) return;
      setConnectionStatus(signalR.HubConnectionState.Connected);
    });

    startConnection();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      textingConversationsHubConnection.stop().catch((error) => {
        console.error("Failed to stop texting conversation connection:", error);
      });
    };
  }, [textingConversationsHubConnection]);

  return { textingConversationsHubConnection, connectionStatus };
};
